import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Star, Briefcase, Plus, X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { activityService } from '@/lib/api/activity.service';
import { guestService } from '@/lib/api/guest.service';
import { serviceService } from '@/lib/api/service.service';
import type { Guest } from '@/types/guest.types';
import type { ActivityWithAssociations, KeyParticipantDto, Vendor } from '@/types/activity.types';

interface ActivityPeopleManagerProps {
  activity: ActivityWithAssociations;
  onUpdate: () => void;
}

export const ActivityPeopleManager: React.FC<ActivityPeopleManagerProps> = ({ 
  activity, 
  onUpdate 
}) => {
  const [loading, setLoading] = useState(false);
    const { t } = useTranslation(['activities']);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [showLeadsDialog, setShowLeadsDialog] = useState(false);
  const [showParticipantsDialog, setShowParticipantsDialog] = useState(false);
  const [showVendorsDialog, setShowVendorsDialog] = useState(false);
  
  // Available items
  const [availableGuests, setAvailableGuests] = useState<Guest[]>([]);
  const [availableVendors, setAvailableVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  
  // Selections
  const [selectedLeadIds, setSelectedLeadIds] = useState<number[]>([]);
  const [selectedParticipants, setSelectedParticipants] = useState<KeyParticipantDto[]>([]);
  const [selectedVendorIds, setSelectedVendorIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Load guests when dialogs open
  useEffect(() => {
    const loadGuests = async () => {
      try {
        const guests = await guestService.getEventGuests(activity.event_id);
        setAvailableGuests(guests);
      } catch (err) {
        console.error('Error loading guests:', err);
      }
    };
    if (showLeadsDialog || showParticipantsDialog) {
      loadGuests();
    }
  }, [showLeadsDialog, showParticipantsDialog, activity.event_id]);

  // Load vendors when dialog opens
  useEffect(() => {
    const loadVendors = async () => {
      try {
        const vendors = await serviceService.getAllVendors() as Vendor[];
        const currentVendorIds = activity.vendors?.map(v => v.id) || [];
        const available = vendors.filter(v => !currentVendorIds.includes(v.id));
        setAvailableVendors(available);
        setFilteredVendors(available);
      } catch (err) {
        console.error('Error loading vendors:', err);
      }
    };
    if (showVendorsDialog) {
      loadVendors();
    }
  }, [showVendorsDialog, activity.vendors]);

  // Filter vendors
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredVendors(availableVendors);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredVendors(availableVendors.filter(v =>
        v.name.toLowerCase().includes(query) ||
        v.service_type?.toLowerCase().includes(query)
      ));
    }
  }, [searchQuery, availableVendors]);

  // Handlers for Leads
  const handleAddLeads = async () => {
    if (selectedLeadIds.length === 0) return;
    setLoading(true);
    try {
      await activityService.assignLeads(activity.id, selectedLeadIds);
      setShowLeadsDialog(false);
      setSelectedLeadIds([]);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.assignLeads'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveLead = async (guestId: number) => {
    setLoading(true);
    try {
      await activityService.removeLead(activity.id, guestId);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.removeLead'));
    } finally {
      setLoading(false);
    }
  };

  // Handlers for Participants
  const handleAddParticipants = async () => {
    if (selectedParticipants.length === 0) return;
    setLoading(true);
    try {
      await activityService.assignKeyParticipants(activity.id, selectedParticipants);
      setShowParticipantsDialog(false);
      setSelectedParticipants([]);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.assignParticipants'));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipant = async (guestId: number) => {
    setLoading(true);
    try {
      await activityService.removeKeyParticipant(activity.id, guestId);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.removeParticipant'));
    } finally {
      setLoading(false);
    }
  };

  // Handlers for Vendors
  const handleTagVendors = async () => {
    if (selectedVendorIds.length === 0) return;
    setLoading(true);
    try {
      await activityService.tagVendors(activity.id, selectedVendorIds);
      setShowVendorsDialog(false);
      setSelectedVendorIds([]);
      setSearchQuery('');
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.tagVendors'));
    } finally {
      setLoading(false);
    }
  };

  const handleUntagVendor = async (vendorId: number) => {
    setLoading(true);
    try {
      await activityService.untagVendor(activity.id, vendorId);
      onUpdate();
    } catch (err: any) {
      setError(err.response?.data?.message || t('activities:peopleManager.errors.untagVendor'));
    } finally {
      setLoading(false);
    }
  };

  // Get filtered guests for leads (exclude already assigned)
  const getAvailableLeadGuests = () => {
    const currentLeadIds = activity.leads?.map(l => l.id) || [];
    return availableGuests.filter(g => !currentLeadIds.includes(g.id));
  };

  // Get filtered guests for participants (exclude already assigned)
  const getAvailableParticipantGuests = () => {
    const currentParticipantIds = activity.key_participants?.map(p => p.guest_id) || [];
    return availableGuests.filter(g => !currentParticipantIds.includes(g.id));
  };

  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <span className="font-semibold text-sm">{t('activities:peopleManager.sections.title')}</span>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {/* 3-Column Grid */}
      <div className="grid grid-cols-3 divide-x">
        {/* Column 1: Leads */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5 text-purple-500" />
              <span className="text-xs font-semibold text-purple-700">{t('activities:peopleManager.columns.leads')}</span>
            </div>
            <Button size="xs" variant="ghost" onClick={() => setShowLeadsDialog(true)} disabled={loading}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-1.5 min-h-[60px]">
            {activity.leads && activity.leads.length > 0 ? (
              activity.leads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-1.5 bg-purple-50 rounded text-xs group">
                  <span className="font-medium text-purple-900 truncate">{lead.name}</span>
                  <button
                    onClick={() => handleRemoveLead(lead.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-purple-100 rounded transition-opacity"
                    disabled={loading}
                  >
                    <X className="h-3 w-3 text-purple-400" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[10px] text-muted-foreground italic">{t('activities:peopleManager.empty.leads')}</p>
            )}
          </div>
        </div>

        {/* Column 2: Key Participants */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Star className="h-3.5 w-3.5 text-yellow-500" />
              <span className="text-xs font-semibold text-yellow-700">{t('activities:peopleManager.columns.keyParticipants')}</span>
            </div>
            <Button size="xs" variant="ghost" onClick={() => setShowParticipantsDialog(true)} disabled={loading}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-1.5 min-h-[60px]">
            {activity.key_participants && activity.key_participants.length > 0 ? (
              activity.key_participants.map((p) => (
                <div key={p.guest_id} className="flex items-center justify-between p-1.5 bg-yellow-50 rounded text-xs group">
                  <div className="truncate">
                    <span className="font-medium text-yellow-900">{p.guest.name}</span>
                    {p.role_description && (
                      <span className="text-yellow-600 ml-1">· {p.role_description}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleRemoveParticipant(p.guest_id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-yellow-100 rounded transition-opacity flex-shrink-0"
                    disabled={loading}
                  >
                    <X className="h-3 w-3 text-yellow-400" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[10px] text-muted-foreground italic">{t('activities:peopleManager.empty.keyParticipants')}</p>
            )}
          </div>
        </div>

        {/* Column 3: Vendors */}
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5 text-blue-500" />
              <span className="text-xs font-semibold text-blue-700">{t('activities:peopleManager.columns.vendors')}</span>
            </div>
            <Button size="xs" variant="ghost" onClick={() => setShowVendorsDialog(true)} disabled={loading}>
              <Plus className="h-3 w-3" />
            </Button>
          </div>
          <div className="space-y-1.5 min-h-[60px]">
            {activity.vendors && activity.vendors.length > 0 ? (
              activity.vendors.map((vendor) => (
                <div key={vendor.id} className="flex items-center justify-between p-1.5 bg-blue-50 rounded text-xs group">
                  <div className="truncate">
                    <span className="font-medium text-blue-900">{vendor.name}</span>
                    {vendor.service_type && (
                      <span className="text-blue-600 ml-1">· {vendor.service_type}</span>
                    )}
                  </div>
                  <button
                    onClick={() => handleUntagVendor(vendor.id)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-blue-100 rounded transition-opacity flex-shrink-0"
                    disabled={loading}
                  >
                    <X className="h-3 w-3 text-blue-400" />
                  </button>
                </div>
              ))
            ) : (
              <p className="text-[10px] text-muted-foreground italic">{t('activities:peopleManager.empty.vendors')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Add Leads Dialog */}
      {showLeadsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full max-h-[70vh] overflow-y-auto">
            <h3 className="font-semibold text-sm mb-3">Add Leads</h3>
            {getAvailableLeadGuests().length === 0 ? (
              <p className="text-sm text-muted-foreground">No available guests</p>
            ) : (
              <div className="space-y-2 mb-4">
                {getAvailableLeadGuests().map((guest) => (
                  <label key={guest.id} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                    selectedLeadIds.includes(guest.id) ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedLeadIds.includes(guest.id)}
                      onChange={() => setSelectedLeadIds(prev =>
                        prev.includes(guest.id) ? prev.filter(id => id !== guest.id) : [...prev, guest.id]
                      )}
                      className="rounded text-purple-600"
                    />
                    <span className="text-sm">{guest.name}</span>
                    <Badge variant="outline" size="sm">{guest.rsvp_status}</Badge>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowLeadsDialog(false); setSelectedLeadIds([]); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddLeads} disabled={loading || selectedLeadIds.length === 0}>
                Add ({selectedLeadIds.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Participants Dialog */}
      {showParticipantsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full max-h-[70vh] overflow-y-auto">
            <h3 className="font-semibold text-sm mb-3">Add Key Participants</h3>
            {getAvailableParticipantGuests().length === 0 ? (
              <p className="text-sm text-muted-foreground">No available guests</p>
            ) : (
              <div className="space-y-2 mb-4">
                {getAvailableParticipantGuests().map((guest) => {
                  const isSelected = selectedParticipants.some(p => p.guest_id === guest.id);
                  return (
                    <div key={guest.id} className={`p-2 rounded border ${isSelected ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200'}`}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => setSelectedParticipants(prev =>
                            isSelected
                              ? prev.filter(p => p.guest_id !== guest.id)
                              : [...prev, { guest_id: guest.id, role_description: '' }]
                          )}
                          className="rounded text-yellow-600"
                        />
                        <span className="text-sm">{guest.name}</span>
                      </label>
                      {isSelected && (
                        <Input
                          placeholder="Role (e.g., Best Man)"
                          value={selectedParticipants.find(p => p.guest_id === guest.id)?.role_description || ''}
                          onChange={(e) => setSelectedParticipants(prev =>
                            prev.map(p => p.guest_id === guest.id ? { ...p, role_description: e.target.value } : p)
                          )}
                          className="mt-2"
                          compact
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowParticipantsDialog(false); setSelectedParticipants([]); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleAddParticipants} disabled={loading || selectedParticipants.length === 0}>
                Add ({selectedParticipants.length})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tag Vendors Dialog */}
      {showVendorsDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-md w-full max-h-[70vh] overflow-y-auto">
            <h3 className="font-semibold text-sm mb-3">Tag Vendors</h3>
            <div className="relative mb-3">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search vendors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
                compact
              />
            </div>
            {filteredVendors.length === 0 ? (
              <p className="text-sm text-muted-foreground">{searchQuery ? 'No vendors found' : 'No available vendors'}</p>
            ) : (
              <div className="space-y-2 mb-4">
                {filteredVendors.map((vendor) => (
                  <label key={vendor.id} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                    selectedVendorIds.includes(vendor.id) ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}>
                    <input
                      type="checkbox"
                      checked={selectedVendorIds.includes(vendor.id)}
                      onChange={() => setSelectedVendorIds(prev =>
                        prev.includes(vendor.id) ? prev.filter(id => id !== vendor.id) : [...prev, vendor.id]
                      )}
                      className="rounded text-blue-600"
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium">{vendor.name}</span>
                      {vendor.service_type && (
                        <Badge variant="outline" size="sm" className="ml-2">{vendor.service_type}</Badge>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setShowVendorsDialog(false); setSelectedVendorIds([]); setSearchQuery(''); }}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleTagVendors} disabled={loading || selectedVendorIds.length === 0}>
                Tag ({selectedVendorIds.length})
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
